import { z } from "zod";
import {
  agentResponseSchema,
  runtimeConfigResponseSchema,
  type AgentResponse,
  type ContinuationReason,
  type PageContext,
  type PageSnapshot,
  type RuntimeConfigResponse,
  type RuntimeEvent,
  type UserActionEvent,
} from "@ai-guidance/shared";
import { readCommerceConfig, type CommerceConfig } from "./commerce/types";
import type { NudgeTrigger } from "./proactivity/text";
import { commerceTextWireSchema, type CommerceTextWireAction } from "./commerce/wire";

export interface SdkOptions {
  appId: string;
  publicKey: string;
  apiBase: string;
  /** true pe siturile NOASTRE de demo (data-demo="true"): întrebările merg pe
   *  /v1/demo/ask cu cookie de sesiune (login), nu pe calea publică anonimă. */
  demo?: boolean;
  /**
   * `data-bottom-offset` de pe tagul de script - px, deja validat/clampat
   * (0-300) în `index.ts`. Ridică ansamblul orb-chat + stratul de prezență
   * deasupra unui element de chrome fix al paginii (bară/banner) pe care CSS-ul
   * din afara shadow root-ului nu-l poate atinge - vezi `orbchat/index.ts` §
   * `bottomOffset`. Panoul clasic nu-l citește: merchantul îl rezolvă deja cu
   * `transform` pe `.aig-root`, iar a-l aplica și acolo ar ridica de două ori.
   */
  bottomOffset?: number;
}

/** Mesajul afișat pe demo când vizitatorul nu e logat (fără cookie de sesiune). */
const DEMO_SIGNIN_MESSAGE =
  "Please sign in to your AskMyWebsite[AI] account to try the live assistant on this demo.";

/**
 * Configul appului plus câmpul `commerce`, care NU trece prin
 * `runtimeConfigResponseSchema`: zod scoate cheile necunoscute, deci l-am pierde
 * exact la parse. Îl citim din JSON-ul brut, defensiv (vezi `readCommerceConfig`).
 * Absent = sit obișnuit, comportamentul de azi.
 */
export interface RuntimeConfig extends RuntimeConfigResponse {
  commerce?: CommerceConfig;
  /**
   * Nudge-urile proactive sunt aprinse pe appul ăsta
   * (docs/plan-commerce-proactivity.md). Ca și `commerce`, câmpul nu trece prin
   * `runtimeConfigResponseSchema` - se citește din JSON-ul brut, iar absența lui
   * (cazul tuturor siturilor) înseamnă că motorul nici nu se instanțiază.
   * Backendul îl trimite doar când appul e ȘI commerce, deci n-are cum să
   * ajungă `true` fără coș din care să se compună textele.
   */
  proactivity?: boolean;
  /**
   * Vocea e aprinsă pe appul ăsta ȘI mediul are cheile providerului - același
   * tipar ca `commerce`: câmpul se citește din JSON-ul brut, iar absența lui
   * (siturile fără voce, backend mai vechi) înseamnă „fără microfon".
   */
  voiceEnabled?: boolean;
}

/**
 * Răspunsul /ask pe un app COMMERCE: același obiect, dar cu acțiunile de coș
 * acceptate. `agentResponseSchema` folosește uniunea de bază, deci un
 * `show_products` ar face parse-ul să arunce și vizitatorul ar vedea „something
 * went wrong" în loc de răspuns. Compus AICI din schemele partajate (nimic nou
 * în `packages/shared`), și folosit doar când appul chiar e magazin - restul
 * siturilor parsează exact ca înainte.
 */
const commerceAgentResponseSchema = z.object({
  message: z.string().max(2000),
  actions: z.array(commerceTextWireSchema).max(10).default([]),
  answersExhausted: z.boolean().default(false),
});

export interface CommerceAgentResponse extends Omit<AgentResponse, "actions"> {
  actions: CommerceTextWireAction[];
}

/** Răspunsul /config când nu e nimic de arătat vizitatorului (not-live / nepublicat). */
export interface ConfigNotAvailable {
  available: false;
  /** `false` doar când backendul a spus explicit că scriptul n-a fost încă confirmat. */
  synced: boolean;
}

/** Discriminant: `RuntimeConfig` are mereu `config`, `ConfigNotAvailable` nu. */
export function isConfigNotAvailable(cfg: RuntimeConfig | ConfigNotAvailable): cfg is ConfigNotAvailable {
  return !("config" in cfg);
}

/**
 * Client pentru runtime API. Toate răspunsurile sunt validate cu schemele
 * partajate - un backend compromis sau un MITM nu poate injecta acțiuni
 * în afara protocolului.
 */
export class RuntimeApi {
  private builderSession: string | null = null;

  constructor(private readonly opts: SdkOptions) {}

  /** Sesiunea de builder marchează cererile ca venind de la adminul sitului. */
  setBuilderSession(token: string | null): void {
    this.builderSession = token;
  }

  /**
   * Appul e magazin (configul a adus `commerce`): de aici încolo răspunsurile
   * /ask se validează cu schema care cunoaște acțiunile de coș. Explicit, nu
   * dedus din răspuns: un sit obișnuit trebuie să parseze EXACT ca înainte.
   */
  private commerce = false;
  setCommerce(enabled: boolean): void {
    this.commerce = enabled;
  }

  private parseAnswer(data: unknown): CommerceAgentResponse {
    return this.commerce ? commerceAgentResponseSchema.parse(data) : agentResponseSchema.parse(data);
  }

  private async request(path: string, init?: RequestInit): Promise<unknown> {
    const url = `${this.opts.apiBase}/v1/apps/${encodeURIComponent(this.opts.appId)}${path}`;
    // FormData (upload de fișiere): NU setăm content-type - fetch pune boundary-ul automat
    const isFormData = init?.body instanceof FormData;
    const res = await fetch(url, {
      ...init,
      headers: {
        ...(isFormData ? {} : { "content-type": "application/json" }),
        "x-public-key": this.opts.publicKey,
        ...(this.builderSession ? { "x-builder-session": this.builderSession } : {}),
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) throw new Error(`[ai-guidance] ${path} failed with ${res.status}`);
    return res.json();
  }

  async exchangeBuilderToken(token: string): Promise<{ sessionToken: string; expiresAt: number }> {
    const data = (await this.request("/builder-session", {
      method: "POST",
      body: JSON.stringify({ token }),
    })) as { sessionToken: string; expiresAt: number };
    return data;
  }

  /**
   * sessionId ca query string, nu body: GET-ul rămâne un GET. Optional: la
   * boot-ul unei pagini proaspete tab-ul încă n-are id (peekChatSessionId în
   * sdk.ts), iar `/config` nu are voie să-l creeze - vizitatorul n-a interacționat
   * încă cu chatul (ePrivacy art. 5(3), vezi chatSession.ts). Când id-ul EXISTĂ
   * deja (conversație în curs dintr-o navigare anterioară), el poartă doar
   * traceId-ul cap-coadă din backend (docs/plan-oplog.md) - nu se creează nimic
   * nou aici.
   */
  /**
   * `{ available: false }` = app-ul nu are widget de arătat vizitatorului acum
   * (nepublicat sau not-live). Backendul întoarce `200 { available: false }`
   * pentru asta, NU 404 - ca să nu apară o eroare roșie în consola clientului
   * pentru o stare normală (asistent încă în configurare). Apelantul (sdk.ts)
   * nu montează nimic, dar citește `synced`: confirmarea de instalare trebuie să
   * plece și înainte de go-live (regresia 30 oct, C9). `synced` lipsă (backend
   * mai vechi) = `true`, adică nu trimitem nimic - doar un `false` explicit
   * declanșează `ackSync`.
   */
  async fetchConfig(sessionId?: string): Promise<RuntimeConfig | ConfigNotAvailable> {
    const query = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : "";
    const data = await this.request(`/config${query}`);
    if (data && typeof data === "object" && (data as { available?: boolean }).available === false) {
      return { available: false, synced: (data as { synced?: unknown }).synced !== false };
    }
    const parsed = runtimeConfigResponseSchema.parse(data);
    const commerce = readCommerceConfig((data as { commerce?: unknown }).commerce);
    // Strict `=== true`: orice altă formă (un backend mai vechi, un câmp
    // stricat) înseamnă „stins", nu „poate".
    const proactivity = (data as { proactivity?: unknown }).proactivity === true;
    const voiceEnabled = (data as { voiceEnabled?: unknown }).voiceEnabled === true;
    return {
      ...parsed,
      ...(commerce ? { commerce } : {}),
      ...(proactivity ? { proactivity: true } : {}),
      ...(voiceEnabled ? { voiceEnabled: true } : {}),
    };
  }

  /** O singură dată per sit, declanșat doar când /config a răspuns cu `synced: false`. */
  async ackSync(): Promise<void> {
    await this.request("/sync-ack", { method: "POST" });
  }

  async ask(
    question: string,
    pageContext: PageContext,
    sessionId: string,
    opts: {
      snapshot?: PageSnapshot;
      continuation?: boolean;
      continuationReason?: ContinuationReason;
      recentActions?: UserActionEvent[];
      /**
       * Conversația a pornit dintr-un nudge proactiv: promptul primește
       * contextul triggerului, ca primul răspuns să continue firul deschis de
       * balon (docs/plan-commerce-proactivity.md). Se atașează O SINGURĂ dată,
       * la prima întrebare reală a vizitatorului - vezi `pendingNudge` în sdk.ts.
       */
      nudge?: NudgeTrigger;
      signal?: AbortSignal;
    } = {},
  ): Promise<CommerceAgentResponse> {
    const { signal, ...body } = opts;
    if (this.opts.demo) return this.demoAsk(question, pageContext, sessionId, body, signal);
    // `signal` ajunge pe fetch, deci Stop chiar închide conexiunea; backendul
    // ascultă `close` pe request și oprește apelul la model (routes/runtime.ts).
    const data = await this.request("/ask", {
      method: "POST",
      body: JSON.stringify({ question, pageContext, sessionId, ...body }),
      signal,
    });
    return this.parseAnswer(data);
  }

  /**
   * Calea de DEMO: nu pe /v1/apps/:appId/ask (public, anonim), ci pe /v1/demo/ask
   * cu cookie-ul de sesiune (`credentials: "include"`) și appId în body. Backendul
   * plafonează + atribuie consumul pe contul logat. Fără cookie (nelogat) sau cont
   * neeligibil → 401/403, iar widgetul afișează un îndemn la login în loc de eroare.
   */
  private async demoAsk(
    question: string,
    pageContext: PageContext,
    sessionId: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<CommerceAgentResponse> {
    const res = await fetch(`${this.opts.apiBase}/v1/demo/ask`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ appId: this.opts.appId, question, pageContext, sessionId, ...body }),
      signal,
    });
    if (res.status === 401 || res.status === 403) {
      // sign-in prompt, nu un răspuns real: contul demo nu e nici el epuizat
      return { message: DEMO_SIGNIN_MESSAGE, actions: [], answersExhausted: false };
    }
    if (!res.ok) throw new Error(`[ai-guidance] /demo/ask failed with ${res.status}`);
    // răspunsul de succes ȘI cel de „limită atinsă" sunt agentResponse valide
    // (mesaj + actions), deci parse-ul e același; câmpurile extra (limited,
    // demoRemaining) sunt ignorate de schemă.
    return this.parseAnswer(await res.json());
  }

  async deleteBuilderDoc(pageId: string, docId: string): Promise<void> {
    await this.request("/builder/delete-doc", {
      method: "POST",
      body: JSON.stringify({ pageId, docId }),
    });
  }

  /**
   * Telemetrie fire-and-forget: cum s-a încheiat o sesiune de ghidare (spotlight),
   * SAU un eveniment de sesiune al widgetului (afișat/deschis - vezi
   * sessionEventSchema în shared).
   * `keepalive: true` - poate pleca exact în momentul în care pagina se descarcă
   * (ex. click pe linkul evidențiat), un fetch obișnuit ar fi anulat de browser
   * odată cu documentul. Răspunsul e 204 și nu se citește - apelantul face
   * `.catch(() => {})`, nu se așteaptă nimic de aici.
   */
  async event(payload: RuntimeEvent): Promise<void> {
    const url = `${this.opts.apiBase}/v1/apps/${encodeURIComponent(this.opts.appId)}/event`;
    await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-public-key": this.opts.publicKey,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  }
}
